import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Flag, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { chatApi } from '../../services/cloudApi';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';

const REPORT_REASONS = ['spam', 'harassment', 'inappropriate', 'fraud', 'other'];

/**
 * Report a chat message (DM or group). Client only sends reason + description.
 */
const ReportMessageModal = ({ isOpen, onClose, messageId }) => {
  const { t } = useTranslation('chat');
  const { authToken } = useAuth();
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setReason('');
    setDescription('');
    setSubmitting(false);
    setError(null);
  }, [isOpen, messageId]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!reason || !messageId || submitting) return;
    if (reason === 'other' && description.trim().length > 500) {
      setError(t('reportModal.descriptionTooLong'));
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await chatApi.reportMessage({
        token: authToken,
        messageId,
        reason,
        description: reason === 'other' ? description.trim() : description.trim() || undefined,
      });
      window.dispatchEvent(
        new CustomEvent('notesplus-show-toast', {
          detail: {
            type: 'success',
            title: t('reportModal.successTitle'),
            message: t('reportModal.successMessage'),
          },
        })
      );
      onClose?.();
    } catch (err) {
      const code = err?.payload?.error || err?.payload?.code || err?.code;
      if (code === 'MESSAGE_ALREADY_REPORTED') {
        setError(t('reportModal.alreadyReported'));
      } else {
        setError(
          getApiErrorMessage(err, { fallback: t('reportModal.submitFailed') })
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const modal = (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100000]"
            onClick={onClose}
          />
          <motion.div
            className="fixed inset-0 flex items-center justify-center z-[100001] p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 8 }}
              transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
              className="w-full max-w-md bg-[var(--color-bg-base)] rounded-2xl border border-white/[0.08] shadow-[0_32px_80px_rgba(0,0,0,0.7)] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-labelledby="report-message-title"
            >
              <div className="flex items-start justify-between p-6 pb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center flex-shrink-0">
                    <Flag className="w-5 h-5 text-amber-400" strokeWidth={2} />
                  </div>
                  <h3
                    id="report-message-title"
                    className="text-base font-semibold text-[var(--color-text-primary)]"
                  >
                    {t('reportModal.title')}
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="p-1.5 rounded-lg text-[var(--color-text-muted)] hover:bg-white/[0.06] hover:text-[var(--color-text-secondary)] transition-colors"
                  aria-label={t('reportModal.cancel')}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-4">
                <p className="text-sm text-[var(--color-text-muted)]">
                  {t('reportModal.description')}
                </p>

                <fieldset className="space-y-2">
                  <legend className="sr-only">{t('reportModal.description')}</legend>
                  {REPORT_REASONS.map((key) => (
                    <label
                      key={key}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer transition-colors ${
                        reason === key
                          ? 'border-[#3d8ef0]/40 bg-[#3d8ef0]/10'
                          : 'border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.04]'
                      }`}
                    >
                      <input
                        type="radio"
                        name="report-reason"
                        value={key}
                        checked={reason === key}
                        onChange={() => setReason(key)}
                        className="accent-[#3d8ef0]"
                      />
                      <span className="text-sm text-[var(--color-text-secondary)]">
                        {t(`reportModal.reasons.${key}`)}
                      </span>
                    </label>
                  ))}
                </fieldset>

                {reason === 'other' && (
                  <div>
                    <label
                      htmlFor="report-description"
                      className="block text-xs font-medium text-[var(--color-text-muted)] mb-1.5"
                    >
                      {t('reportModal.otherLabel')}
                    </label>
                    <textarea
                      id="report-description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value.slice(0, 500))}
                      maxLength={500}
                      rows={3}
                      placeholder={t('reportModal.otherPlaceholder')}
                      className="w-full rounded-xl border border-white/[0.08] bg-[#161f31] px-3 py-2.5 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:border-[#3d8ef0]/40 resize-none"
                    />
                    <p className="mt-1 text-[11px] text-[var(--color-text-muted)] text-end">
                      {description.length}/500
                    </p>
                  </div>
                )}

                {error && (
                  <p className="text-sm text-red-400" role="alert">
                    {error}
                  </p>
                )}

                <div className="flex items-center justify-end gap-2 pt-1">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={submitting}
                    className="px-4 py-2 rounded-xl text-sm font-medium text-[var(--color-text-secondary)] hover:bg-white/[0.06] transition-colors"
                  >
                    {t('reportModal.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={!reason || submitting}
                    className="px-4 py-2 rounded-xl text-sm font-semibold bg-[#3d8ef0] text-white hover:bg-[#5ca3f5] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {submitting ? t('reportModal.submitting') : t('reportModal.submit')}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(modal, document.body);
};

export default ReportMessageModal;
